// @ts-check

import { computed, unref } from 'vue';

/** @typedef {`#${string}`} Hex */

/**
 * Is light or dark colour
 *
 * @param {import('vue').Ref<Hex> | Hex} colour hexadecimal
 * @returns true if colour is light
 */
export function useLightOrDark(colour) {
  const matchThreeOrSix = /^#([0-9a-f]{6}|[0-9a-f]{3})$/gi;
  const getThree = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/gi;

  return computed(() => {
    if (!unref(colour) || !unref(colour).match(matchThreeOrSix)) {
      return true;
    }

    const hex = unref(colour).replaceAll(getThree, '#$1$1$2$2$3$3');

    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);

    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

    return yiq >= 128;
  });
}

/**
 * Get a contrasting colour
 *
 * @param {import('vue').Ref<Hex> | Hex} colour hexadecimal
 * @param {import('vue').Ref<string> | string} [light] hexadecimal
 * @param {import('vue').Ref<string> | string} [dark] hexadecimal
 */
export function useContrastColour(colour, light = '#fff', dark = '#000') {
  return computed(() => (useLightOrDark(colour).value ? unref(dark) : unref(light)));
}
